import {
    CoreEnv,
    CoreRequest,
    CoreUser,
    GASettingKeys,
} from '@eventbrite/context-gen';
import { History, Location } from 'history';
import React from 'react';
import { match } from 'react-router-dom';
import { EVENTBRITE_REFINEMENT_TO_CATEGORY } from './constants/categories';
import { SUBCATEGORY_REFINEMENT_TO_KEY } from './constants/subcategories';
import {
    EnvProvider,
    LocationProvider,
    RouteProvider,
    SearchProvider,
} from './context';
import { useLocationByLocationSlug } from './hooks/queries/location';
import { parseEventSearchFromUrlParams } from './utils';
export interface CategoryBrowseRouteParams {
    locationSlug: string;
    category: string;
    subcategory: string;
}
export type CategoryBrowseRouteMatch = match<CategoryBrowseRouteParams>;
interface CategoryBrowseProviderProps {
    match: match<CategoryBrowseRouteParams>;
    location: Location;
    history: History;
    user: CoreUser;
    env: CoreEnv;
    gaSettings: GASettingKeys;
    request: CoreRequest;
    app_version?: string;
    app_name: string;
    isBotRequest: boolean;
    location_slug: string;
    location_data: {
        displayLocation: string;
        city: string;
        region: string;
        country: string;
        latitude: number;
        longitude: number;
        isOnline: boolean;
        placeId: string;
    };
}

const getSearchParams = (
    location: Location,
    match: CategoryBrowseRouteMatch,
) => {
    const params = new URLSearchParams(location.search);
    let { activeTag, date, customDate } = parseEventSearchFromUrlParams(params);

    if (!activeTag) {
        const isSubcategory = !!match.params.subcategory;
        const activeRefinement = isSubcategory
            ? match.params.subcategory
            : match.params.category;
        const REFINEMENT_MAP: Record<string, string> = isSubcategory
            ? SUBCATEGORY_REFINEMENT_TO_KEY
            : EVENTBRITE_REFINEMENT_TO_CATEGORY;
        activeTag = REFINEMENT_MAP[activeRefinement];
    }

    return { activeTag, date, customDate };
};

export const CategoryBrowseProviders: React.FunctionComponent<
    CategoryBrowseProviderProps
> = (props) => {
    const {
        match,
        location,
        history,
        user,
        env,
        gaSettings,
        request,
        app_version,
        app_name,
        location_data,
        isBotRequest,
    } = props;

    const { activeTag, date, customDate } = getSearchParams(location, match);

    const initialLocation = !!location_data
        ? {
              currentPlace:
                  location_data?.displayLocation || location_data?.city,
              currentPlaceParent:
                  location_data?.displayLocation === location_data?.region
                      ? location_data?.country
                      : location_data?.region,
              slug: match.params.locationSlug,
              latitude: location_data?.latitude,
              longitude: location_data?.longitude,
              isOnline: location_data?.isOnline,
              placeId: location_data?.placeId,
          }
        : {};

    //Initialize location if hydrated via props
    useLocationByLocationSlug(match.params.locationSlug, initialLocation);

    return (
        <EnvProvider
            env={env}
            gaSettings={gaSettings}
            request={request}
            user={user}
            app_version={app_version}
            app_name={app_name}
            isBotRequest={isBotRequest}
        >
            <RouteProvider
                match={match}
                location={location}
                history={history}
                parsedValues={{
                    activeTag,
                    date,
                    customDate,
                }}
            >
                <LocationProvider locationSlug={match.params.locationSlug}>
                    <SearchProvider
                        initialQuery={{
                            tags: [activeTag || ''],
                            dates:
                                date === 'current_future'
                                    ? 'current_future'
                                    : ['current_future', date],
                            date_range: customDate,
                        }}
                    >
                        {props.children}
                    </SearchProvider>
                </LocationProvider>
            </RouteProvider>
        </EnvProvider>
    );
};
